import useWorkspaceSubscriptionMutations from "features/subscriptions/datahooks/useWorkspaceSubscriptionMutations";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import { formatDate } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { RentedSubscription, Subscription } from "common/types";

export default function RemoveChangeButton({
  type,
  subscription,
}: {
  type: "cancellation" | "change";
  subscription: Subscription | RentedSubscription;
}) {
  const { removeChange, isRemovingChange } =
    useWorkspaceSubscriptionMutations();

  const isDesktop = useTwBreakpoint("lg");

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <Component>
      <Trigger asChild>
        <Button variant="outline-black" size={isDesktop ? "sm" : "md"}>
          Remove {type}
        </Button>
      </Trigger>

      <Content>
        <Title>Remove scheduled {type}?</Title>

        <Description className="mb-8">
          {type === "change"
            ? `Are you sure you want to remove your scheduled change for 
          ${formatDate(subscription.next_billing * 1000)}?`
            : `Are you sure you want to remove your scheduled cancellation? Your subscription will become active and renew normally on ${formatDate(subscription.cancelled_at * 1000)}`}
        </Description>

        <div className="flex justify-between gap-x-2.5">
          <Close asChild>
            <Button
              variant="secondary-black"
              className="lg:flex-1"
              disabled={isRemovingChange}
            >
              Cancel
            </Button>
          </Close>

          <Button
            className="lg:flex-1"
            isLoading={isRemovingChange}
            onClick={() => removeChange({ subscription })}
          >
            Remove {type}
          </Button>
        </div>
      </Content>
    </Component>
  );
}
